import type React from "react";
import { useContext } from "react";

import { Typography } from "@mui/material";
import { Stack } from "@mui/system";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";

import { AnatomyMan, AnatomyWoman } from "assets";
import { CurrentPatientContext } from "utils/contexts";
import { translatePainLocation, translateTreatment } from "utils/translations";

import { PainMarkers } from "../PainMarkers";

const Container = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  ${props => props.theme.belowMobileBreakpoint} {
    flex-direction: column;
  }
`;

const Image = styled.div`
  flex: 3;
  background-color: ${props => props.theme.colors.redesign.db5};
  border-radius: 16px;
  position: relative;
  padding: 16px 0;
  margin: auto 0;
  display: flex;
  align-items: center;
  justify-content: center;
  ${props => props.theme.belowMobileBreakpoint} {
    margin: 0 auto;
    flex: unset;
    width: 185px;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 185px;
`;

const Woman = styled(AnatomyWoman)`
  width: 185px;
`;

const Man = styled(AnatomyMan)`
  width: 185px;
`;

const TextSection = styled.div`
  flex: 2;
`;

interface PainLocation {
  lateral_location: "left" | "right" | "undefined";
  location:
    | "undefined"
    | "neck"
    | "spine"
    | "lower_back"
    | "upper_back"
    | "knee"
    | "hip"
    | "shoulder"
    | "foot"
    | "elbow"
    | "hand"
    | "jaw";
}

type LateralLocation = Exclude<PainLocation["lateral_location"], "undefined">;
type JointLocation = Exclude<PainLocation["location"], "undefined">;

export const Overview: React.FC = () => {
  const { patient } = useContext(CurrentPatientContext);
  const { t } = useTranslation();
  const theme = useTheme();

  // TODO: fix so it workes like mostPainful location
  const painfulJoints = patient?.pain_locations
    ?.filter((location: PainLocation) => location.location !== "undefined")
    .map((location: PainLocation) => (
      <Typography variant="subtitle1" key={location.lateral_location + location.location}>
        {`${translatePainLocation({ painLocation: location.location as JointLocation })}${
          location.lateral_location === "undefined"
            ? ""
            : `: ${t(`patients.lateral_location.${location.lateral_location as LateralLocation}`).toLowerCase()}`
        }`}
      </Typography>
    ))
    .reverse();

  const gender = patient?.gender;

  const painMarkers = patient?.pain_locations?.map((location: PainLocation) => (
    <PainMarkers
      key={location.lateral_location + location.location}
      side={location.lateral_location}
      location={location.location}
    />
  ));

  const mostPainful = () => {
    const location = patient?.most_painful_location?.location;
    const lateral = patient?.most_painful_location?.lateral_location;
    if (!location) return null;
    return translateTreatment({ ailment: null, lateral_location: lateral, location });
  };

  return (
    <Container data-testid="overview">
      <Image>
        <ImageWrapper>
          {gender === "male" ? <Man data-testid="image-male" /> : <Woman data-testid="image-female" />}
          {painMarkers}
        </ImageWrapper>
      </Image>
      <TextSection>
        {patient ? (
          <>
            <Stack gap={1} borderBottom={`1px solid ${theme.colors.redesign.db20}`} padding="16px 0">
              <Typography variant="subtitle1" sx={{ color: theme.colors.redesign.db60 }}>
                {t("patients.most_painful")}
              </Typography>
              <Typography variant="subtitle1">{mostPainful()}</Typography>
            </Stack>
            <Stack gap={1} borderBottom={`1px solid ${theme.colors.redesign.db20}`} padding="16px 0">
              <Typography variant="subtitle1" sx={{ color: theme.colors.redesign.db60 }}>
                {t("patients.joint_pain")}
              </Typography>
              {painfulJoints}
            </Stack>
          </>
        ) : (
          <div>{t("errors.generic")}</div>
        )}
      </TextSection>
    </Container>
  );
};
